import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api, { apiErr, isInternal } from "@/lib/api";
import { numLang } from "@/lib/num";
import { dcoa, dtax, dtaxCode } from "@/lib/masterI18n";
import { MasterImportModal } from "@/components/MasterImportModal";
import { LedgerTab } from "@/components/LedgerTab";
import { BankTab } from "@/components/BankTab";
import { OpeningBalanceTab } from "@/components/OpeningBalanceTab";
import { KmkTab } from "@/components/KmkTab";
import { useTableTools, TableToolbar, SelectAllBox, SelectBox, deleteEach } from "@/components/TableTools";
import { fl } from "@/lib/finI18n";
import { loadKop } from "@/lib/kop";
import { ComboBox } from "@/components/ComboBox";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, FileType2, Upload } from "lucide-react";

const COA_TYPES = [
  { v: "asset", l: "Aset / Asset / 资产" }, { v: "liability", l: "Kewajiban / Liability / 负债" }, { v: "equity", l: "Ekuitas / Equity / 权益" },
  { v: "revenue", l: "Pendapatan / Revenue / 收入" }, { v: "expense", l: "Beban / Expense / 费用" },
];

export default function MasterData() {
  const { user, selectedClientId, clients } = useAuth();
  const [sp] = useSearchParams();
  const { t, lang } = useI18n();
  const clientId = selectedClientId;
  const canInternal = isInternal(user.role);
  const [data, setData] = useState({ coa: [], banks: [], projects: [], taxes: [] });
  const [groups, setGroups] = useState([]);
  useEffect(() => { api.get(`/coa-groups?lang=${lang}`).then(({ data: g }) => setGroups(g)).catch(() => {}); }, [lang]);
  const groupOpts = groups.map((g) => ({ v: g.key, l: `${g.section_label} › ${g.label}` }));
  const [kops, setKops] = useState([]);
  useEffect(() => { loadKop(lang).then((d) => setKops(d.items)); }, [lang]);
  const kopOpts = [{ value: "", label: "—" }, ...kops.map((k) => ({ value: k.code, label: `${k.code} · ${k.label}` }))];

  const load = useCallback(async () => {
    if (!clientId) return;
    const [c, b, p, tx] = await Promise.all([
      api.get(`/coa?client_id=${clientId}`), api.get(`/banks?client_id=${clientId}`),
      api.get(`/projects?client_id=${clientId}`), api.get(`/taxes?client_id=${clientId}`),
    ]);
    setData({ coa: c.data, banks: b.data, projects: p.data, taxes: tx.data });
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const clientName = clients.find((c) => c.id === clientId)?.name || "";

  return (
    <div className="space-y-4" data-testid="master-page">
      <div className="page-sticky">
        <h1 className="font-head text-2xl font-bold tracking-tight">{t("master.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("common.client")}: {clientName}</p>
      </div>

      <Tabs key={sp.get("tab") || "default"} defaultValue={sp.get("tab") || (canInternal ? "coa" : "banks")}>
        <TabsList className="flex-wrap h-auto">
          {canInternal && <TabsTrigger value="coa" data-testid="md-tab-coa">{t("master.tab.coa")}</TabsTrigger>}
          <TabsTrigger value="ledgers" data-testid="md-tab-ledgers">{fl("ledger", lang)}</TabsTrigger>
          <TabsTrigger value="banks" data-testid="md-tab-banks">{t("master.tab.banks")}</TabsTrigger>
          <TabsTrigger value="projects" data-testid="md-tab-projects">{t("master.tab.projects")}</TabsTrigger>
          {canInternal && <TabsTrigger value="taxes" data-testid="md-tab-taxes">{t("master.tab.taxes")}</TabsTrigger>}
          {canInternal && <TabsTrigger value="opening" data-testid="md-tab-opening">{t("master.tab.opening")}</TabsTrigger>}
          <TabsTrigger value="kmk" data-testid="md-tab-kmk">{fl("kmkTitle", lang)}</TabsTrigger>
        </TabsList>

        <TabsContent value="coa">
          <MasterTable title={t("master.coaTitle")} type="coa" clientId={clientId} items={data.coa} onDone={load}
            columns={[["code", t("common.code")], ["name", t("common.name")], ["group", fl("group", lang), "group"], ["type", t("common.type"), "type"]]}
            fields={[["code", t("common.code")], ["name", t("common.name")], ["type", t("common.type"), "select", COA_TYPES], ["group", fl("group", lang), "select", groupOpts]]}
            endpoint="/coa" defaults={{ type: "expense" }} />
        </TabsContent>
        <TabsContent value="ledgers"><LedgerTab clientId={clientId} coa={data.coa} /></TabsContent>
        <TabsContent value="banks"><BankTab clientId={clientId} items={data.banks} onDone={load} /></TabsContent>
        <TabsContent value="projects">
          <MasterTable title={t("master.projectsTitle")} type="projects" clientId={clientId} items={data.projects} onDone={load}
            columns={[["code", t("common.code")], ["name", t("common.name")]]}
            fields={[["name", t("mf.projectName")], ["code", t("common.code")]]}
            endpoint="/projects" />
        </TabsContent>
        <TabsContent value="taxes">
          <MasterTable title={t("master.taxesTitle")} type="taxes" clientId={clientId} items={data.taxes} onDone={load}
            readOnly={user.role !== "super_admin"} note={t("master.taxLocked")}
            columns={[["code", t("common.code")], ["name", t("common.name")], ["rate", t("mf.rate"), "rate"], ["kop", fl("kop", lang), "kop"]]}
            fields={[["name", t("common.name")], ["code", t("common.code")], ["rate", t("mf.rate"), "number"], ["kop", fl("kop", lang), "combo", kopOpts]]}
            endpoint="/taxes" defaults={{ rate: 0 }} />
        </TabsContent>
        {canInternal && <TabsContent value="opening"><OpeningBalanceTab clientId={clientId} /></TabsContent>}
        <TabsContent value="kmk"><KmkTab clientId={clientId} banks={data.banks} /></TabsContent>
      </Tabs>
    </div>
  );
}

export async function downloadMasterPdf(clientId, type) {
  try {
    const res = await api.get(`/export/master?client_id=${clientId}&type=${type}&fmt=pdf&lang=${numLang()}`, { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(res.data); a.download = `${type}.pdf`; a.click();
  } catch (e) { toast.error("Gagal export"); }
}

function MasterTable({ title, type, clientId, items, columns, fields, endpoint, defaults, onDone, readOnly, note }) {
  const { t, lang } = useI18n();
  const [importOpen, setImportOpen] = useState(false);
  const show = (it, k, kind) => (kind === "kop" ? (it[k] || "—") : kind === "type" ? t(`coa.${it[k]}`) : kind === "group" ? (it.group_label?.[lang] || it[k]) : kind === "rate" ? `${it[k]}%`
    : (k === "name" && type === "coa") ? dcoa(it.code, it[k], lang, it) : (k === "name" && type === "taxes") ? dtax(it.code, it[k], lang, it)
    : (k === "code" && type === "taxes") ? dtaxCode(it, lang) : it[k]);
  const tools = useTableTools(items, { date: (r) => r.created_at, text: (it) => [...columns.map(([k, , kind]) => show(it, k, kind)), it.name, it.code, it.name_en, it.name_zh, it.code_en, it.code_zh].join(" ") });
  const del = async (id) => {
    if (!window.confirm("Hapus data ini?")) return;
    try { await api.delete(`${endpoint}/${id}`); toast.success("Dihapus"); onDone(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const bulkDel = async (ids) => { const r = await deleteEach(ids, (id) => api.delete(`${endpoint}/${id}`)); onDone(); return r; };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-head font-semibold">{title} <span className="text-muted-foreground font-normal text-sm">({tools.view.length})</span></h2>
        <TableToolbar tools={tools} id={`md-${type}`} filename={type} onDelete={readOnly ? undefined : bulkDel} cols={columns.map(([k, l, kind]) => [l, (it) => show(it, k, kind)])}>
          <Button variant="outline" size="sm" className="h-9" onClick={() => downloadMasterPdf(clientId, type)} data-testid={`md-export-pdf-${type}`}><FileType2 className="h-4 w-4 mr-1" />PDF</Button>
          {!readOnly && <Button variant="outline" size="sm" className="h-9" onClick={() => setImportOpen(true)} data-testid={`md-import-${type}`}><Upload className="h-4 w-4 mr-1" />Import</Button>}
          {!readOnly && <MDForm clientId={clientId} endpoint={endpoint} fields={fields} defaults={defaults} onDone={onDone} />}
        </TableToolbar>
      </div>
      {note && <p className="mb-3 rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground" data-testid={`md-note-${type}`}>{note}</p>}
      <div className="overflow-auto rounded-md border border-border">
        <Table>
          <TableHeader><TableRow><TableHead className="w-8">{!readOnly && <SelectAllBox tools={tools} id={`md-${type}`} />}</TableHead>{columns.map(([k, l]) => <TableHead key={k}>{l}</TableHead>)}<TableHead className="text-right">{!readOnly && t("common.actions")}</TableHead></TableRow></TableHeader>
          <TableBody>
            {tools.view.length === 0 && <TableRow><TableCell colSpan={columns.length + 2} className="text-center py-8 text-muted-foreground">{t("common.noData")}</TableCell></TableRow>}
            {tools.view.map((it) => (
              <TableRow key={it.id} data-testid={`md-row-${type}-${it.id}`}>
                <TableCell>{!readOnly && <SelectBox tools={tools} row={it} id={`md-${type}`} />}</TableCell>
                {columns.map(([k, , kind]) => (
                  <TableCell key={k} className={kind === "rate" ? "num text-xs" : kind ? "text-xs text-muted-foreground" : "text-sm"}>{show(it, k, kind)}</TableCell>
                ))}
                <TableCell className="text-right whitespace-nowrap">
                  {!readOnly && <>
                  <MDForm clientId={clientId} endpoint={endpoint} fields={fields} defaults={defaults} editItem={it} onDone={onDone}
                    trigger={<Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`md-edit-${type}-${it.id}`}><Pencil className="h-4 w-4" /></Button>} />
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => del(it.id)} data-testid={`md-del-${type}-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <MasterImportModal open={importOpen} onOpenChange={setImportOpen} clientId={clientId} type={type} label={title} onImported={onDone} />
    </Card>
  );
}

function MDForm({ clientId, endpoint, fields, defaults, editItem, onDone, trigger }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const init = () => {
    const base = { ...(defaults || {}) };
    fields.forEach(([k]) => { base[k] = base[k] ?? ""; });
    return editItem ? { ...base, ...Object.fromEntries(fields.map(([k]) => [k, editItem[k]])) } : base;
  };
  const [f, setF] = useState(init());
  useEffect(() => { if (open) setF(init()); }, [open, editItem]);

  const save = async () => {
    try {
      const payload = { client_id: clientId, ...f };
      fields.forEach(([k, , type]) => { if (type === "number") payload[k] = Number(f[k] || 0); });
      if (editItem) await api.put(`${endpoint}/${editItem.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(t("common.saved")); setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || <Button size="sm" className="h-9" data-testid={`md-add-${endpoint.replace("/", "")}`}><Plus className="h-4 w-4 mr-1" />{t("common.add")}</Button>}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{editItem ? t("common.edit") : t("common.add")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {fields.map(([k, l, type, opts]) => (
            <div key={k} className="space-y-1.5"><Label>{l}</Label>
              {type === "combo" ? (
                <ComboBox testid={`field-${k}`} value={f[k] || ""} onChange={(v) => setF({ ...f, [k]: v })} options={opts} placeholder="—" />
              ) : type === "select" ? (
                <Select value={f[k]} onValueChange={(v) => setF({ ...f, [k]: v })}>
                  <SelectTrigger data-testid={`field-${k}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{opts.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input type={type === "number" ? "number" : "text"} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`field-${k}`} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter><Button onClick={save} data-testid="md-save">{t("common.save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
